import React, { useEffect, useRef, useState } from "react";
import { Paperclip, X, Loader2, FileText, Music, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("norea_token")}` });

function useBlobUrl(src, enabled) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let u;
    fetch(src, { headers: authHeaders() }).then((r) => r.blob()).then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [src, enabled]);
  return url;
}

function FileItem({ src, att, onRemove, testid }) {
  const isImg = (att.mime_type || "").startsWith("image/");
  const isAudio = (att.mime_type || "").startsWith("audio/") || /\.mp3$/i.test(att.filename || "");
  const url = useBlobUrl(src, isImg || isAudio);
  const open = () => { if (url) window.open(url, "_blank"); else fetch(src, { headers: authHeaders() }).then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank")); };
  const remove = (
    <button type="button" onClick={onRemove} className="absolute -top-1.5 -right-1.5 bg-white border border-norea-border rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600" title="Supprimer" data-testid={`${testid}-remove`}>
      <X className="w-3 h-3" />
    </button>
  );
  if (isImg) {
    return (
      <div className="relative group" data-testid={testid}>
        <button type="button" onClick={open} className="block w-20 h-20 rounded border border-norea-border overflow-hidden bg-norea-bg" title={att.filename}>
          {url ? <img src={url} alt={att.filename} className="w-full h-full object-cover" /> : <Loader2 className="w-4 h-4 m-auto animate-spin text-norea-textMuted" />}
        </button>
        {remove}
      </div>
    );
  }
  if (isAudio) {
    return (
      <div className="relative group flex items-center gap-2 px-2 py-1 rounded border border-norea-border bg-white" data-testid={testid}>
        <Music className="w-3.5 h-3.5 text-norea-primary" />
        {url ? <audio controls src={url} className="h-8 max-w-[220px]" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {remove}
      </div>
    );
  }
  return (
    <div className="relative group" data-testid={testid}>
      <button type="button" onClick={open} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-norea-border bg-white hover:bg-norea-surfaceHover text-[11px]" title={att.filename}>
        <FileText className="w-3.5 h-3.5 text-norea-primary" /> <span className="truncate max-w-[120px]">{att.filename}</span>
      </button>
      {remove}
    </div>
  );
}

export function FilesField({ field, dossierId, tab, items, onChange }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("field_id", field.id);
        fd.append("file", file);
        await api.post(`/dossiers/${dossierId}/tab-data/${tab}/attachments`, fd);
      }
      toast.success(files.length > 1 ? `${files.length} fichiers ajoutés` : "Fichier ajouté");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };
  const remove = async (id) => {
    try { await api.delete(`/dossiers/${dossierId}/tab-data/${tab}/attachments/${id}`); onChange?.(); } catch { toast.error("Suppression impossible"); }
  };
  const isAudio = (field.accept || "").includes("audio");
  return (
    <div className="flex flex-wrap items-center gap-2">
      {(items || []).map((a) => (
        <FileItem key={a.id} att={a} src={`${API}/dossiers/${dossierId}/tab-data/${tab}/attachments/${a.id}`} onRemove={() => remove(a.id)} testid={`file-${field.id}-${a.id}`} />
      ))}
      <button type="button" onClick={() => ref.current?.click()} disabled={uploading} data-testid={`file-add-${field.id}`}
        className={`inline-flex items-center justify-center gap-1 rounded border border-dashed border-norea-border text-[11px] text-norea-textMuted hover:bg-norea-surfaceHover hover:text-norea-primary transition-colors ${(field.accept || "").includes("image") ? "w-20 h-20 flex-col" : "px-2 py-1.5"}`}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : isAudio ? <Music className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
        {isAudio ? "Ajouter un audio" : "Ajouter"}
      </button>
      <input ref={ref} type="file" multiple accept={field.accept || undefined} onChange={upload} className="hidden" data-testid={`file-input-${field.id}`} />
    </div>
  );
}

export function CallClientButton({ dossierId, phones }) {
  const [busy, setBusy] = useState(false);
  const list = (phones || []).filter(Boolean);
  const call = async (num) => {
    setBusy(true);
    try {
      const r = await api.post("/ringover/click-to-call", { to_number: num, dossier_id: dossierId });
      if (r.data?.ok) toast.success("Ringover fait sonner votre poste puis compose le numéro");
      else toast.error(r.data?.error || "Appel refusé par Ringover");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Appel impossible");
    } finally {
      setBusy(false);
    }
  };
  if (!list.length) return <div className="text-xs text-norea-textMuted italic">Aucun numéro client</div>;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((n, i) => (
        <button key={n} type="button" onClick={() => call(n)} disabled={busy} className="btn-secondary text-xs" data-testid={`install-call-btn-${i}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />} Appeler {n}
        </button>
      ))}
    </div>
  );
}
